import React from 'react';
import { 
  formatAddressSingleLine, 
  formatAddressMultiLine, 
  formatAddressCompact,
  hasAddressData,
  type AddressData 
} from '../../utils/addressFormatter';

interface AddressDisplayProps {
  addressData: AddressData;
  format?: 'single' | 'multi' | 'compact';
  maxLength?: number;
  emptyText?: string;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Reusable component for displaying addresses in different formats
 */
export const AddressDisplay: React.FC<AddressDisplayProps> = ({
  addressData,
  format = 'single',
  maxLength = 50,
  emptyText = '-',
  className = '',
  style = {}
}) => {
  if (!hasAddressData(addressData)) {
    return (
      <span className={className} style={{ color: 'var(--text-secondary)', ...style }}>
        {emptyText}
      </span>
    );
  }

  let formattedAddress: string;
  let displayStyle: React.CSSProperties = { ...style };

  switch (format) {
    case 'multi':
      formattedAddress = formatAddressMultiLine(addressData);
      displayStyle = { 
        ...displayStyle, 
        whiteSpace: 'pre-line',
        lineHeight: '1.4'
      };
      break;
    
    case 'compact':
      formattedAddress = formatAddressCompact(addressData, maxLength);
      displayStyle = { 
        ...displayStyle, 
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      };
      break;
    
    case 'single':
    default:
      formattedAddress = formatAddressSingleLine(addressData);
      break;
  }

  return (
    <span className={className} style={displayStyle} title={formattedAddress}>
      {formattedAddress}
    </span>
  );
};

/**
 * Specialized component for table cells with address
 */
export const AddressTableCell: React.FC<{
  addressData: AddressData;
  showCountry?: boolean;
}> = ({ addressData, showCountry = true }) => {
  if (!hasAddressData(addressData)) {
    return <span style={{ color: 'var(--text-secondary)' }}>-</span>;
  }

  return (
    <div className="text-card-body">
      {addressData.address && (
        <div style={{ marginBottom: '4px' }}>
          <AddressDisplay 
            addressData={{ address: addressData.address }} 
            format="compact"
            maxLength={60}
          />
        </div>
      )}
      {showCountry && addressData.country && (
        <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
          {addressData.country}
        </div>
      )}
    </div>
  );
};

/**
 * Specialized component for card displays with full address
 */
export const AddressCardDisplay: React.FC<{
  addressData: AddressData;
  label?: string;
}> = ({ addressData, label = 'Address' }) => {
  if (!hasAddressData(addressData)) return null;

  return (
    <div>
      {label && (
        <div className="text-label" style={{ marginBottom: '4px' }}>
          {label}
        </div>
      )}
      <AddressDisplay 
        addressData={addressData} 
        format="multi"
        className="text-card-body"
      />
    </div>
  );
};

export default AddressDisplay;